/**
 * The last-mile link matrix: one row per physical WAN port.
 *
 * This is the only place the carrier behind a link is named — Cato reports it
 * as `tunnelRemoteIPInfo.provider`, derived from the tunnel's public source IP.
 * When a site is degraded, this table is where the "which ISP is it?" question
 * gets answered without opening the Cato console.
 *
 * It is also the only place quality is shown *per port* rather than per site.
 * The site-level average of a good fibre and a sick DSL is a mildly unwell
 * site; these rows say which of the two is at fault, and — via the last-mile
 * columns — whether the fault is the carrier's or Cato's.
 */

import { Badge, DataGrid, EmptyState, Skeleton, Typography } from "@norican/react";

import type { NetworkLink } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { formatDuration, formatMbps, formatMs, formatPct, lossTone, rttTone } from "./status";

interface LinkMatrixProps {
  /** Every WAN port in the estate, from `/network/links`. */
  links: NetworkLink[];
  /** True while the links query is still in flight. */
  isPending?: boolean;
}

export function LinkMatrix({ links, isPending = false }: LinkMatrixProps) {
  const down = links.filter((link) => link.connected === false).length;

  return (
    <PanelCard
      title="Last-mile links"
      caption={
        links.length === 0 ? undefined : (
          <>
            {`${links.length} WAN ${links.length === 1 ? "port" : "ports"} · ${down} down`}
            <span className="block">
              Last-mile latency and loss are measured Socket→internet, not through Cato — a bad
              number there is the ISP, not the SASE fabric.
            </span>
          </>
        )
      }
      padding="none"
    >
      {isPending && links.length === 0 ? (
        <div className="p-4">
          <Skeleton height="12rem" />
        </div>
      ) : links.length === 0 ? (
        <div className="p-6">
          <EmptyState
            size="sm"
            title="No WAN ports reported"
            description="No Socket in this account is reporting a physical WAN interface."
          />
        </div>
      ) : (
        <div className="h-[28rem]">
          {/* DataGrid's scroller is `max-height: 100%`, so pinned columns and the
              sticky header only get a scroll region inside a DEFINITE height.
              Without one they latch onto the page viewport and drift while the
              page scrolls. Every DataGrid story wraps the grid this way. */}
          <DataGrid<NetworkLink>
            aria-label="Last-mile WAN ports"
            data={links}
            getRowId={(link) => link.id}
            getRowSearchText={(link) =>
              `${link.site_name} ${link.isp ?? ""} ${link.physical_port ?? ""}`
            }
            density="compact"
            columns={[
              {
                id: "site",
                header: "Site",
                accessor: (link) => link.site_name,
                sortable: true,
                pinned: "left",
                cell: (link) => (
                  <div className="flex flex-col">
                    <span className="text-fg font-medium">{link.site_name}</span>
                    <span className="text-fg-muted text-xs">{link.device_name ?? "—"}</span>
                  </div>
                ),
              },
              {
                id: "port",
                header: "Port",
                accessor: (link) => link.physical_port ?? link.name ?? null,
                sortable: true,
                cell: (link) => (
                  <Typography variant="code" as="span" className="text-fg text-xs">
                    {link.physical_port ?? link.name ?? "—"}
                  </Typography>
                ),
              },
              {
                id: "state",
                header: "Tunnel",
                accessor: (link) => (link.connected === false ? 0 : 1),
                sortable: true,
                cell: (link) => (
                  <Badge
                    tone={
                      link.connected === false ? "danger" : link.connected ? "success" : "neutral"
                    }
                    variant="subtle"
                    size="sm"
                  >
                    {link.connected === false ? "Down" : link.connected ? "Up" : "Unknown"}
                  </Badge>
                ),
              },
              {
                id: "isp",
                header: "Carrier",
                accessor: (link) => link.isp ?? null,
                sortable: true,
                cell: (link) => (
                  <div className="flex flex-col">
                    <span className="text-fg">{link.isp ?? "Unknown ISP"}</span>
                    <span className="text-fg-muted text-xs">
                      {[link.city, link.country].filter(Boolean).join(", ") ||
                        link.tunnel_remote_ip ||
                        "—"}
                    </span>
                  </div>
                ),
              },
              {
                // Sits beside the carrier on purpose: "WAN2 (Orange) losing 3%"
                // is the sentence this pair of cells has to read as.
                id: "loss",
                header: "Loss ↓",
                align: "end",
                numeric: true,
                accessor: (link) => link.metrics?.lost_downstream_pct ?? null,
                sortable: true,
                cell: (link) => (
                  <div className="flex flex-col items-end">
                    <Badge
                      tone={lossTone(link.metrics?.lost_downstream_pct)}
                      variant="subtle"
                      size="sm"
                    >
                      {formatPct(link.metrics?.lost_downstream_pct)}
                    </Badge>
                    <span className="text-fg-muted text-xs tabular-nums">
                      ↑ {formatPct(link.metrics?.lost_upstream_pct)} · jitter{" "}
                      {formatMs(link.metrics?.jitter_upstream)}
                    </span>
                  </div>
                ),
              },
              {
                // Discards are not loss: the packets were dropped by a full
                // queue rather than lost in flight, which is what "voice is
                // choppy but the line looks fine" actually is. Cato only
                // publishes these as a timeseries, so the figure is the window
                // mean rather than a single reading.
                id: "discards",
                header: "Discards ↑",
                align: "end",
                numeric: true,
                accessor: (link) => link.metrics?.packets_discarded_upstream_pct ?? null,
                sortable: true,
                cell: (link) => (
                  <div className="flex flex-col items-end">
                    <Badge
                      tone={lossTone(link.metrics?.packets_discarded_upstream_pct)}
                      variant="subtle"
                      size="sm"
                    >
                      {formatPct(link.metrics?.packets_discarded_upstream_pct)}
                    </Badge>
                    <span className="text-fg-muted text-xs tabular-nums">
                      ↓ {formatPct(link.metrics?.packets_discarded_downstream_pct)}
                    </span>
                  </div>
                ),
              },
              {
                id: "last-mile",
                header: "Last mile (ISP)",
                align: "end",
                numeric: true,
                accessor: (link) => link.metrics?.last_mile_latency_ms ?? null,
                sortable: true,
                cell: (link) => (
                  <div className="flex flex-col items-end">
                    <Badge
                      tone={rttTone(link.metrics?.last_mile_latency_ms)}
                      variant="subtle"
                      size="sm"
                    >
                      {formatMs(link.metrics?.last_mile_latency_ms)}
                    </Badge>
                    <span className="text-fg-muted text-xs tabular-nums">
                      {formatPct(link.metrics?.last_mile_loss_pct)} loss
                    </span>
                  </div>
                ),
              },
              {
                id: "uptime",
                header: "Tunnel uptime",
                align: "end",
                numeric: true,
                accessor: (link) => link.tunnel_uptime_ms ?? null,
                sortable: true,
                cell: (link) => (
                  <span className="text-fg tabular-nums">
                    {formatDuration(
                      link.tunnel_uptime_ms == null ? null : link.tunnel_uptime_ms / 1000,
                    )}
                  </span>
                ),
              },
              {
                id: "capacity",
                header: "Provisioned",
                align: "end",
                numeric: true,
                accessor: (link) => link.downstream_bandwidth_mbps ?? null,
                sortable: true,
                cell: (link) => (
                  <span className="text-fg-muted text-xs tabular-nums">
                    {formatMbps(link.downstream_bandwidth_mbps)} down ·{" "}
                    {formatMbps(link.upstream_bandwidth_mbps)} up
                  </span>
                ),
              },
              {
                id: "pop",
                header: "PoP",
                accessor: (link) => link.pop_name ?? null,
                sortable: true,
                cell: (link) => (
                  <div className="flex flex-col">
                    <span className="text-fg text-sm">{link.pop_name ?? "—"}</span>
                    {link.previous_pop_name && link.previous_pop_name !== link.pop_name && (
                      <span className="text-warning-fg text-xs">
                        moved from {link.previous_pop_name}
                      </span>
                    )}
                  </div>
                ),
              },
              {
                id: "reason",
                header: "Last reconnect reason",
                accessor: (link) => link.tunnel_connection_reason ?? null,
                cell: (link) =>
                  link.tunnel_connection_reason ? (
                    <Typography
                      variant="code"
                      as="span"
                      className={
                        link.connected === false
                          ? "text-danger-fg text-xs"
                          : "text-fg-muted text-xs"
                      }
                    >
                      {link.tunnel_connection_reason}
                    </Typography>
                  ) : (
                    <span className="text-fg-subtle text-xs">—</span>
                  ),
              },
            ]}
          />
        </div>
      )}
    </PanelCard>
  );
}
